import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, UserRole } from '../auth/AuthContext';
import { ApiError } from '../api/client';
import { Logo } from '../components/Logo';
import { PasswordField } from '../components/PasswordField';
import { Spinner } from '../components/ui/Spinner';
import { registerSchema, validate } from '../lib/validation';

const VALID_ROLES: UserRole[] = ['client', 'deliverer', 'business'];

export const RegisterPage = () => {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRole = ((): UserRole => {
    const r = params.get('role');
    return r && VALID_ROLES.includes(r as UserRole) ? (r as UserRole) : 'client';
  })();
  const [form, setForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    role: initialRole
  });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    navigate('/orders', { replace: true });
  }, [user, navigate]);

  if (user) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 text-slate-500">
        <Spinner />
        <p className="text-sm">Redirection…</p>
      </div>
    );
  }

  const handleChange = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const result = validate(registerSchema, form);
    if (!result.ok) {
      setFieldErrors(result.errors);
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    try {
      await register(result.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Erreur d'inscription");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto grid max-w-4xl gap-8 lg:grid-cols-5 lg:gap-0 animate-fade-in">
      <div className="auth-panel relative hidden overflow-hidden rounded-2xl border-r-0 lg:col-span-2 lg:flex lg:flex-col lg:rounded-r-none">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-brand-700 to-brand-500" />
        <div className="absolute inset-0 opacity-25 mix-blend-soft-light bg-[radial-gradient(circle_at_20%_20%,white,transparent_55%)]" />
        <div className="relative flex flex-1 flex-col justify-end p-8 text-white">
          <Logo tone="white" />
          <h2 className="mt-3 text-2xl font-semibold leading-tight">Une plateforme pour clients, livreurs et entreprises</h2>
          <p className="mt-3 text-sm text-slate-100/90 max-w-xs">
            Créez votre profil en quelques secondes et commencez à suivre vos opérations.
          </p>
        </div>
      </div>

      <div className="auth-panel p-8 sm:p-10 lg:col-span-3 lg:rounded-l-none lg:border-l-0">
        <p className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            <span aria-hidden>←</span>
            Présentation de la plateforme
          </Link>
        </p>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Créer un compte</h1>
        <p className="mt-1 text-sm text-slate-500">Les champs marqués d’une astérisque sont obligatoires.</p>

        <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label>
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Prénom</span>
              <input className="input mt-1.5" value={form.firstName} onChange={handleChange('firstName')} aria-invalid={!!fieldErrors.firstName} />
              {fieldErrors.firstName && <p className="mt-1 text-xs text-red-600">{fieldErrors.firstName}</p>}
            </label>
            <label>
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Nom</span>
              <input className="input mt-1.5" value={form.lastName} onChange={handleChange('lastName')} aria-invalid={!!fieldErrors.lastName} />
              {fieldErrors.lastName && <p className="mt-1 text-xs text-red-600">{fieldErrors.lastName}</p>}
            </label>
          </div>
          <label>
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Email</span>
            <input type="email" className="input mt-1.5" value={form.email} onChange={handleChange('email')} aria-invalid={!!fieldErrors.email} />
            {fieldErrors.email && <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>}
          </label>
          <label>
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Téléphone</span>
            <input className="input mt-1.5" value={form.phoneNumber} onChange={handleChange('phoneNumber')} aria-invalid={!!fieldErrors.phoneNumber} />
            {fieldErrors.phoneNumber && <p className="mt-1 text-xs text-red-600">{fieldErrors.phoneNumber}</p>}
          </label>
          <PasswordField
            label="Mot de passe (min. 8 caractères)"
            autoComplete="new-password"
            value={form.password}
            onChange={(password) => setForm((prev) => ({ ...prev, password }))}
            invalid={!!fieldErrors.password}
            error={fieldErrors.password}
          />
          <label>
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Rôle</span>
            <select className="input mt-1.5" value={form.role} onChange={handleChange('role')}>
              <option value="client">Client</option>
              <option value="deliverer">Livreur</option>
              <option value="business">Entreprise</option>
            </select>
          </label>
          {error && (
            <div
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              role="alert"
            >
              {error}
            </div>
          )}
          <button type="submit" className="btn-primary mt-2 w-full sm:w-auto" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner className="h-4 w-4 border-2 border-white border-t-transparent" />
                Création…
              </>
            ) : (
              'Créer mon compte'
            )}
          </button>
        </form>
        <p className="mt-8 text-sm text-slate-600">
          Déjà un compte ?{' '}
          <Link to="/login" className="font-medium text-brand-600 hover:text-brand-700">
            Se connecter
          </Link>
        </p>
      </div>
    </div>
  );
};
